<template>
  <div class="bg-card bg-white border-card-border md:px-6">
    <div class="w-full pt-3">
      <div>
        <h1 slot="header" class="text-xl font-semibold pb-2 mt-5">Tell us about work</h1>
        <h3 slot="header" class="pb-3 border-b mb-4">
          Please provide the following information to find out if you pre-qualify for a loan.
        </h3>
      </div>
      <form autocomplete="on" class="w-full pt-3 mt-4" novalidate @submit.prevent="next">
        <Input
          :key="employmentInformation.jobTitle.key"
          v-model="employmentInformation.jobTitle.value"
          type="text"
          :id="employmentInformation.jobTitle.id"
          :getter="$getters.jobTitle"
          :error="employmentInformation.jobTitle.error"
          :maxlength="employmentInformation.jobTitle.maxlength"
          :name="employmentInformation.jobTitle.name"
          :placeholder="employmentInformation.jobTitle.placeholder"
          :p="employmentInformation.jobTitle.p"
          :setter="$setters.jobTitle"
          :underlined="true"
          class="mb-3"
        />
        <div class="inline-flex">
          <div class="pt-1 px-2 mb-3">
            <RadioButton
              optionName="Full Time"
              id="full"
              v-model="employmentInformation.employmentStatus.value"
            ></RadioButton>
            <RadioButton
              optionName="Part Time"
              id="part"
              v-model="employmentInformation.employmentStatus.value"
            ></RadioButton>
          </div>
        </div>
        <transition name="in--down">
          <div
            v-if="employmentStatusError.length > 0"
            :class="`pin-r -translate-y-50 bg-white
            text-red-400 text-xs rounded font-semibold z-50 pb-2`"
          >
            {{ employmentStatusError }}
          </div>
        </transition>
        <div class="mb-6">
          <DatePicker
            v-model="employmentInformation['startDate'].value"
            :name="employmentInformation['startDate'].name"
            :id="employmentInformation['startDate'].id"
            :error="employmentInformation['startDate'].error"
            :disabledDates="{
              from: new Date(),
              to: this.getToDate()
            }"
            @selectedDate="this.setStartDate"
            format="MM/yyyy"
          />
        </div>
        <div v-for="item in fieldNames" :key="employmentInformation[item].key">
          <Input
            v-model="employmentInformation[item].value"
            type="text"
            :id="employmentInformation[item].id"
            :getter="$getters[item]"
            :error="employmentInformation[item].error"
            :onkeypress="
              ['salary', 'additionalIncome'].includes(item)
                ? 'return event.charCode >= 48 && event.charCode <= 57'
                : ''
            "
            :maxlength="employmentInformation[item].maxlength"
            :name="employmentInformation[item].name"
            :placeholder="employmentInformation[item].placeholder"
            :p="
              item !== 'employeeId'
                ? employmentInformation[item].p
                : 'This is your employee ID number used for payroll.'
            "
            :prefix="['salary', 'additionalIncome'].includes(item) ? '$' : ''"
            :setter="$setters[item]"
            :underlined="true"
            class="mb-4"
          />
        </div>
        <Dropdown
          v-model="employmentInformation.payFrequency.value"
          v-bind:dropdownOptions="payFrequencyOptions"
          :id="employmentInformation.payFrequency.id"
          :value="employmentInformation.payFrequency.value"
          :error="employmentInformation.payFrequency.error"
          :placeholder="employmentInformation.payFrequency.placeholder"
          :name="employmentInformation.payFrequency.name"
          type="text"
          :underlined="true"
          inputClass="mt-4 sm:mt-0"
          class="mb-3"
        />
        <Dropdown
          v-model="employmentInformation.payMonths.value"
          v-bind:dropdownOptions="payMonthsOptions"
          :value="employmentInformation.payMonths.value"
          :id="employmentInformation.payMonths.id"
          :error="employmentInformation.payMonths.error"
          :placeholder="employmentInformation.payMonths.placeholder"
          :name="employmentInformation.payMonths.name"
          :p="employmentInformation.payMonths.p"
          type="text"
          :underlined="true"
          inputClass="mt-4 sm:mt-0"
          class="mb-3"
        />

        <div>Are either you or your spouse active duty service members?</div>
        <div class="inline-flex">
          <div class="pt-1 mb-3 -ml-2">
            <RadioButton
              optionName="Yes"
              id="Yes"
              v-model="employmentInformation.militaryStatus.value"
            ></RadioButton>
            <RadioButton
              optionName="No"
              id="No"
              v-model="employmentInformation.militaryStatus.value"
            ></RadioButton>
          </div>
        </div>
        <transition name="in--down">
          <div
            v-if="employmentInformation.militaryStatus.error.length > 0"
            :class="`pin-r -translate-y-50 bg-white
            text-red-400 text-xs rounded font-semibold z-50 pb-2`"
          >
            {{ employmentInformation.militaryStatus.error }}
          </div>
        </transition>
      </form>

      <div class="flex w-full mt-4">
        <button
          class="hover:text-primary mr-auto focus:outline-none rounded"
          type="btn"
          aria-label="navigate back"
          v-on:click="handleBack()"
        >
          <i
            class="fas fa-arrow-left bg-gray-300 rounded-full flex items-center text-lg justify-center w-8 h-8"
          ></i>
        </button>

        <base-button
          type="primary ml-auto"
          :disabled="!nextEnabled"
          :loading="nextLoading"
          aria-label="navigate next"
          @click="handleNext()"
        >
          Next
        </base-button>
      </div>
      <transition name="in--up">
        <div v-if="!nextEnabled" class="flex items-center justify-center w-full px-4">
          <h3 class="text-red-500 text-sm">Please fix all errors before proceeding.</h3>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Engine from '@/mixins/Engine'
import Dropdown from '@/components/VIVA/Dropdown.vue'
import RadioButton from '@/components/VIVA/RadioButton.vue'

// import Input from '../VIVA/Input.vue'

export default {
  // components: { Input },
  name: 'EmploymentInformation',
  mixins: [Engine],
  components: {
    Dropdown,
    RadioButton
  },
  // props: {
  //   initialData: Object
  // },
  data: function () {
    return {
      employmentInformation: {
        jobTitle: {
          name: 'Job Title',
          placeholder: 'Teacher',
          error: '',
          value: '',
          optional: false,
          id: 'job_title'
        },
        employmentStatus: {
          name: 'Status',
          placeholder: 'Full Time',
          error: '',
          value: 'full',
          optional: false,
          id: 'employment_status'
        },
        startDate: {
          maxlength: 7,
          name: 'Start Date',
          placeholder: 'MM/YYYY',
          error: '',
          value: '',
          optional: false,
          p: 'The month and year that you started this job',
          id: 'start_date'
        },
        employeeId: {
          maxlength: 20,
          name: 'Employee ID',
          placeholder: '372001',
          error: '',
          value: '',
          optional: false,
          id: 'employee_id'
        },
        salary: {
          maxlength: 9,
          name: 'Annual Base Salary',
          placeholder: '40,000',
          error: '',
          value: '',
          optional: false,
          p:
            'This is your annual income before any tax and other deductions. ' +
            'It excludes bonuses and commissions. If you are a full-time, hourly ' +
            'employee this number can be calculated by multiplying your hourly pay rate by 2080 hours.',
          id: 'salary'
        },
        additionalIncome: {
          maxlength: 9,
          name: 'Additional Income',
          placeholder: '1,000',
          error: '',
          optional: true,
          value: '',
          p: '(OPTIONAL) Approximate annual income you earn from your employer beyond your basic salary - i.e. overtime, bonuses, and commissions.',
          id: 'additional_income'
        },
        payMonths: {
          name: 'How many months do you receive a paycheck?',
          placeholder: '',
          p: 'If you are paid year round, please select "12"',
          optional: false,
          error: '',
          value: '',
          id: 'pay_months'
        },
        payFrequency: {
          name: 'How often do you receive a paycheck?',
          placeholder: '',
          p: '',
          optional: false,
          error: '',
          value: '',
          id: 'pay_frequency'
        },
        militaryStatus: {
          name: 'Are either you or your spouse active duty service members?',
          placeholder: '',
          p: 'Are either you or your spouse active duty service members?',
          optional: false,
          error: '',
          value: '',
          id: 'military_status'
        },
        ddAccounts: {
          name: 'Number of Direct Deposit Accounts',
          maxlength: 1,
          placeholder: '1, 2, 3, etc.',
          p: 'How many bank accounts do you send your check to from your current employer?',
          optional: false,
          error: '',
          value: '',
          id: 'dd_accounts'
        },
        choaDepartmentExtension: {
          name: 'Department Extension',
          maxlength: 4,
          placeholder: '5555',
          error: '',
          optional: !this.$store.getters['applicationData/isChoa'],
          value: '',
          p: 'This is the last 4 digits of your work phone number.',
          id: 'choa_department_extension'
        },
        choaCostCenter: {
          name: 'Cost Center Number',
          maxlength: 5,
          placeholder: '22222',
          error: '',
          optional: !this.$store.getters['applicationData/isChoa'],
          value: '',
          p: 'This is a 5 digit number that can be found on your timecard.',
          id: 'choa_costCenter'
        }
      },
      fieldOrder: ['employeeId', 'salary', 'additionalIncome'],
      choaOrder: ['choaDepartmentExtension', 'choaCostCenter'],
      nextLoading: false,
      firstClick: true,
      payMonthsOptions: ['10', '11', '12'],
      payFrequencyOptions: ['Weekly', '2 Weeks', 'Twice a Month', 'Monthly'],
      militaryStatusOptions: [
        'No (Neither I nor my spouse are active duty service members)',
        'Yes (I and/or my spouse are active duty service members)'
        // TODO: Record veteran status?
      ]
    }
  },
  computed: {
    isChoa() {
      return this.$store.getters['applicationData/isChoa']
    },
    fieldNames() {
      // This skips the first two fields in employmentInformation to handle the check boxes
      // This determines if the choa fields are added or not
      let fieldNames = this.fieldOrder
      if (this.isChoa) {
        fieldNames.push(...this.choaOrder)
      }
      fieldNames.push('ddAccounts')
      return fieldNames
    },
    jobTitle() {
      return this.employmentInformation.jobTitle.value
    },
    employeeId() {
      return this.employmentInformation.employeeId.value
    },
    startDate() {
      return this.employmentInformation.startDate.value
    },
    salary() {
      return this.employmentInformation.salary.value
    },
    ddAccounts() {
      return this.employmentInformation.ddAccounts.value
    },
    payMonths() {
      return this.employmentInformation.payMonths.value
    },
    payFrequency() {
      return this.employmentInformation.payFrequency.value
    },

    militaryStatus() {
      return this.employmentInformation.militaryStatus.value
    },
    choaCostCenter() {
      return this.employmentInformation.choaCostCenter.value
    },
    choaDepartmentExtension() {
      return this.employmentInformation.choaDepartmentExtension.value
    },
    employmentStatus() {
      return this.employmentInformation.employmentStatus.value
    },
    employmentStatusError() {
      return this.employmentInformation.employmentStatus.error
    },
    fieldsValid() {
      const errorsExist = Object.keys(this.employmentInformation).filter(
        (fn) => this.employmentInformation[fn].error.length > 0
      )
      const emptyRequired = Object.keys(this.employmentInformation).filter(
        (fn) =>
          !this.employmentInformation[fn].optional &&
          this.employmentInformation[fn].value.length === 0
      )
      return errorsExist.length + emptyRequired.length === 0
    },
    nextEnabled() {
      return this.firstClick || this.fieldsValid
    }
  },
  methods: {
    monthDiff(d1, d2) {
      var months
      months = (d2.getFullYear() - d1.getFullYear()) * 12
      months -= d1.getMonth()
      months += d2.getMonth()
      return months <= 0 ? 0 : months + 1
    },
    checkEmploymentPeriod() {
      if (this.employmentInformation.employmentStatus.value === 'part') {
        return 'part-time'
      }

      const months = this.employmentInformation.startDate.value.getUTCMonth() + 1
      const year = this.employmentInformation.startDate.value.getUTCFullYear()
      let employeeStartDate = new Date(year, months)
      let currentDate = new Date()
      let monthDiffernece = this.monthDiff(employeeStartDate, currentDate)
      if (monthDiffernece < 12) {
        return 'min-period'
      }
      return 'accepted'
    },
    async handleNext() {
      this.nextLoading = true
      if (this.firstClick) {
        this.firstClick = false
        const emptyRequired = Object.keys(this.employmentInformation).filter(
          (fieldName) =>
            !this.employmentInformation[fieldName].optional &&
            this.employmentInformation[fieldName].value.length === 0
        )
        const existingErrors = Object.keys(this.employmentInformation).filter(
          (fieldName) => this.employmentInformation[fieldName].error.length > 0
        )
        if (emptyRequired.length + existingErrors.length > 0) {
          emptyRequired.forEach((requiredField) => {
            this.employmentInformation[requiredField].error = 'This is a required field.'
          })
          this.nextLoading = false
          return
        }
        if (this.employmentInformation.salary.error) {
          this.nextLoading = false
          return
        }
      }
      let checkEmployment = this.checkEmploymentPeriod()

      const outputData = {}
      Object.keys(this.employmentInformation).forEach((item) => {
        if (this.employmentInformation[item].value) {
          outputData[item] = this.employmentInformation[item].value
        }
      })

      let month = this.employmentInformation.startDate.value.getUTCMonth() + 1
      const year = this.employmentInformation.startDate.value.getUTCFullYear()
      if (month < 10) {
        month = `0${month}`
      }
      outputData.startDate = new Date(`${year}-${month}-01`).toISOString().split('T')[0]
      outputData.salary = Math.round(outputData.salary)
      if (outputData.additionalIncome) {
        outputData.additionalIncome = Math.round(outputData.additionalIncome)
      }
      if (this.employmentInformation.militaryStatus.value) {
        const militaryStatus = this.employmentInformation.militaryStatus.value
        if (militaryStatus.startsWith('Yes')) {
          outputData.activeMilitary = true
        } else {
          outputData.activeMilitary = false
        }
      }
      if (this.employmentInformation.payFrequency.value) {
        const mapping = {
          Weekly: 'weekly',
          '2 Weeks': 'biweekly',
          'Twice a Month': 'semimonthly',
          Monthly: 'monthly'
        }
        outputData.payFrequency = mapping[this.employmentInformation.payFrequency.value]
      }

      this.$store.commit('applicationData/updateEmploymentInformation', outputData)
      this.$store
        .dispatch('applicationData/uploadEmploymentInformation', outputData)
        .then(({ serverError, validationError, nextRoute, data, requirePaystubs }) => {
          if (serverError) throw new Error(`Server Error: ${serverError}`)
          if (validationError && validationError.length) {
            validationError.forEach((invalidField) => {
              this.employmentInformation[invalidField].error = 'Invalid value'
            })

            throw new Error('Validation Error')
          }
          this.$store.commit('applicationData/setRequirePaystubs', requirePaystubs)
          this.$store.commit('applicationData/setTenureProof', data)
          console.log('serverError: ', serverError)
          console.log('validationError: ', validationError)

          console.log('employmentInfo uploaded')
          this.updateFinished = true
          window.scrollTo({ top: 0, behavior: 'smooth' })
          this.nextLoading = false
          this.$emit('next', nextRoute)
        })
        .catch((e) => {
          console.log(`FE Error in employmentInformation/updateEmploymentInformation: ${e}`)
          console.warn(e)
        })
        .finally(() => {
          this.nextLoading = false
        })
    },
    handleBack() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      this.$emit('back')
    },
    getToDate() {
      const year = 1950
      const month = new Date().getMonth()
      const day = new Date().getDate()
      return new Date(year, month, day)
    },
    setStartDate(v) {
      this.employmentInformation['startDate'].value = v
    }
  },
  mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' })

    const initialData = {}
    Object.keys(this.employmentInformation).map((k) => {
      let v = this.$store.state.applicationData[k]
      if (v != undefined) initialData[k] = v
    })
    if (initialData.startDate) {
      initialData.startDate = new Date(initialData.startDate)
    }
    if (initialData.militaryStatus != undefined) {
      initialData.militaryStatus = initialData.militaryStatus === 'Yes' ? 'Yes' : 'No'
    }
    console.log('initial data: ', initialData)
    Object.keys(initialData).forEach((k) => {
      if (
        !['additionalIncome', 'salary', 'ddAccounts', 'payMonths', 'payFrequency'].includes(k) &&
        this.$setters[k]
      ) {
        this.employmentInformation[k].value = this.$setters[k](initialData[k])
      } else {
        this.employmentInformation[k].value = initialData[k]
      }
    })
  },
  watch: {
    employmentStatus: {
      handler(v) {
        if (v.length > 0) {
          this.employmentInformation.employmentStatus.error = ''
        }
      }
    },
    jobTitle: {
      handler(v) {
        if (v.length > 0) {
          this.employmentInformation.jobTitle.error = ''
        } else {
          this.employmentInformation.jobTitle.error = 'Please enter your Job Title'
        }
      }
    },
    employeeId: {
      handler(v) {
        if (v.length > 0) {
          this.employmentInformation.employeeId.error = ''
        } else {
          this.employmentInformation.employeeId.error = 'Please enter a valid Employee ID.'
        }
      }
    },
    salary: {
      handler(v) {
        if (Number(v) < 1000 || isNaN(Number(v))) {
          this.employmentInformation.salary.error = 'Please enter a valid base salary'
        } else {
          this.employmentInformation.salary.error = ''
        }
      }
    },
    choaCostCenter: {
      handler(v) {
        if (v.length < 5) {
          this.employmentInformation.choaCostCenter.error =
            'Please enter a valid cost center number'
        } else {
          this.employmentInformation.choaCostCenter.error = ''
        }
      }
    },
    payMonths: {
      handler(v) {
        if (v.length > 0) {
          this.employmentInformation.payMonths.error = ''
        } else {
          this.employmentInformation.payMonths.error = 'Please fill this out.'
        }
      }
    },
    payFrequency: {
      handler(v) {
        if (v.length > 0) {
          this.employmentInformation.payFrequency.error = ''
        } else {
          this.employmentInformation.payFrequency.error = 'Please fill this out.'
        }
      }
    },
    militaryStatus: {
      handler(v) {
        if (v.length > 0) {
          this.employmentInformation.militaryStatus.error = ''
        } else {
          this.employmentInformation.militaryStatus.error = 'Please fill this out.'
        }
      }
    },
    ddAccounts: {
      handler(v) {
        if (v.length > 0) {
          this.employmentInformation.ddAccounts.error = ''
        } else {
          this.employmentInformation.ddAccounts.error = 'Please fill this out.'
        }
      }
    },
    choaDepartmentExtension: {
      handler(v) {
        if (v.length < 4) {
          this.employmentInformation.choaDepartmentExtension.error =
            'Please enter a valid department extension'
        } else {
          this.employmentInformation.choaDepartmentExtension.error = ''
        }
      }
    }
  }
}
</script>
